<template>
  <div class="blog-wrapper">

    <b-overlay
        :show="isLoading"
        rounded="sm"
    >
      <b-alert
          variant="warning"
          :show="!isLoading && (isEmpty || loadError)"
      >
        <div class="alert-body">
          {{ loadError ? 'Ошибка загрузки фида.' : 'Фид недоступен — проверьте YA_TOKEN_MY на сервере.' }}
        </div>
      </b-alert>

      <!-- Сгенерированные плейлисты -->
      <b-card v-if="playlists.length" no-body>
        <b-card-header class="pb-50">
          <h5>Плейлисты для вас</h5>
        </b-card-header>
        <b-table
            :items="playlists"
            responsive
            :fields="playlistColumns"
            primary-key="id"
            show-empty
            empty-text="Нет плейлистов"
            class="position-relative mb-0"
        >
          <template #cell(playlist)="data">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                    size="32"
                    :src="data.item.cover"
                    :text="avatarText(data.item.title)"
                    variant="primary"
                />
              </template>
              <span class="font-weight-bold d-block">{{ data.item.title }}</span>
              <small class="text-muted">{{ data.item.description }}</small>
            </b-media>
          </template>

          <template #cell(actions)="data">
            <div class="text-nowrap">
              <b-button
                  :to="{name: 'yandex-playlist-view', params: {id: data.item.id, owner_id: data.item.owner_id, streaming: 'yandex'}}"
                  variant="primary"
                  class="btn-icon"
                  size="sm"
              >
                <feather-icon icon="ArrowRightCircleIcon"/>
              </b-button>
              <b-button
                  :href="`https://music.yandex.ru/playlists/${data.item.uuid}`"
                  variant="warning"
                  class="btn-icon ml-1"
                  size="sm"
                  target="_blank"
              >
                <feather-icon icon="CastIcon"/>
              </b-button>
            </div>
          </template>
        </b-table>
      </b-card>

      <!-- События фида по дням -->
      <div
          v-for="day in days"
          v-if="day.events.length"
          :key="day.day"
      >
        <h4 class="my-1">{{ day.day }}</h4>

        <b-card
            v-for="(event, eventIndex) in day.events"
            :key="`${day.day}-${eventIndex}`"
            no-body
            class="mb-1"
        >
          <b-card-header class="pb-50">
            <h5 class="mb-0">{{ event.title }}</h5>
            <b-button
                v-if="event.type === 'tracks'"
                v-b-toggle="`event-${day.day}-${eventIndex}`"
                variant="flat-primary"
                size="sm"
            >
              Треки [{{ event.tracks.length }}]
            </b-button>
          </b-card-header>

          <!-- Подборка треков (сворачиваемая) -->
          <b-collapse
              v-if="event.type === 'tracks'"
              :id="`event-${day.day}-${eventIndex}`"
          >
            <feed-tracks-table
                :tracks="event.tracks"
                @add-track="addTrack"
            />
          </b-collapse>

          <!-- Рекомендации артистов -->
          <b-card-body v-else-if="event.type === 'artists'">
            <div
                v-for="(artist, index) in event.artists"
                :key="index"
                class="mb-50"
            >
              <b-link
                  v-if="artist.id"
                  :to="{name: 'artist-view', params: {id: artist.id}}"
                  class="font-weight-bold"
              >{{ artist.name }}</b-link>
              <span v-else class="font-weight-bold">{{ artist.name }}</span>
              <small
                  v-if="artist.genres && artist.genres.length"
                  class="text-muted"
              > — {{ artist.genres.join(', ') }}</small>
              <b-link
                  :href="`https://music.yandex.ru/artist/${artist.yandex_id}`"
                  target="_blank"
                  class="ml-50"
              >
                <feather-icon icon="ExternalLinkIcon"/>
              </b-link>
            </div>
          </b-card-body>

          <!-- Альбомы -->
          <b-table
              v-else-if="event.type === 'albums'"
              small
              :items="event.albums"
              :fields="albumColumns"
              class="mb-0"
          >
            <template #cell(album)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                      square
                      size="32"
                      :src="data.item.cover"
                      :text="avatarText(data.item.title)"
                  />
                </template>
                <b-link
                    :href="`https://music.yandex.ru/album/${data.item.yandex_id}`"
                    target="_blank"
                    class="font-weight-bold d-block"
                >{{ data.item.title }}</b-link>
                <small class="text-muted">{{ data.item.genre }}</small>
              </b-media>
            </template>

            <template #cell(artists)="data">
              <span v-for="(artist, index) in data.item.artists" :key="index">
                <b-link
                    v-if="artist.id"
                    :to="{name: 'artist-view', params: {id: artist.id}}"
                    class="font-weight-bold"
                >{{ artist.name }}</b-link>
                <span v-else>{{ artist.name }}</span>
                <span v-if="index < data.item.artists.length - 1">, </span>
              </span>
            </template>

            <template #cell(actions)="data">
              <b-button
                  variant="flat-primary"
                  size="sm"
                  @click="toggleAlbumTracks(data)"
              >
                Треки [{{ data.item.track_count }}]
              </b-button>
            </template>

            <template #row-details="data">
              <b-overlay
                  :show="Boolean(albumTracksLoading[data.item.yandex_id])"
                  rounded="sm"
              >
                <feed-tracks-table
                    :tracks="albumTracks[data.item.yandex_id] || []"
                    @add-track="track => addAlbumTrack(data.item.yandex_id, track)"
                />
              </b-overlay>
            </template>
          </b-table>
        </b-card>
      </div>
    </b-overlay>

  </div>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCollapse,
  BLink,
  BMedia,
  BOverlay,
  BTable,
  VBToggle,
} from 'bootstrap-vue'
import {avatarText} from '@core/utils/filter'
import store from '@/store'
import {computed, onUnmounted, ref} from '@vue/composition-api'
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import musicStoreModule from '@/views/db-music/musicStoreModule'
import FeedTracksTable from '@/views/db-music/FeedTracksTable.vue'

export default {
  components: {
    BAlert,
    BAvatar,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCollapse,
    BLink,
    BMedia,
    BOverlay,
    BTable,
    FeedTracksTable,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  setup() {
    const STORE_MODULE_NAME = 'playlist'
    const toast = useToast()

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const isLoading = ref(true)
    const loadError = ref(false)
    const playlists = ref([])
    const days = ref([])

    const isEmpty = computed(() => playlists.value.length === 0 && days.value.length === 0)

    const playlistColumns = [
      {key: 'playlist', label: 'Плейлист'},
      {key: 'tracks_count', label: 'Треков'},
      {key: 'actions', label: ''},
    ]
    const albumColumns = [
      {key: 'album', label: 'Альбом'},
      {key: 'artists', label: 'Артисты'},
      {key: 'year', label: 'Год'},
      {key: 'actions', label: ''},
    ]

    const albumTracks = ref({})
    const albumTracksLoading = ref({})

    const fetchFeedData = () => {
      isLoading.value = true
      loadError.value = false

      return store.dispatch('playlist/yandexFeed')
          .then(response => {
            playlists.value = response.data.playlists || []
            days.value = response.data.days || []
          })
          .catch(error => {
            console.error(error)
            loadError.value = true
          })
          .finally(() => {
            isLoading.value = false
          })
    }

    const showErrorToast = title => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }

    // Создать трек в БД (с автосозданием первого артиста, как в PlaylistView).
    const createTrackInDb = async track => {
      const firstArtist = (track.artists && track.artists[0]) || null

      let artistId = firstArtist && firstArtist.id
      if (!artistId) {
        const response = await store.dispatch('playlist/createArtist', {title: firstArtist && firstArtist.name})
        artistId = response.data.data.id
      }
      await store.dispatch('playlist/addTrack', {id: artistId, queryParams: {title: track.title}})
    }

    const addTrack = async track => {
      isLoading.value = true
      try {
        await createTrackInDb(track)
        await fetchFeedData()
      } catch (error) {
        console.error(error)
        showErrorToast('Error adding track')
        isLoading.value = false
      }
    }

    const loadAlbumTracks = async albumId => {
      albumTracksLoading.value = {...albumTracksLoading.value, [albumId]: true}
      try {
        const response = await store.dispatch('playlist/yandexFeedAlbumTracks', {albumId})
        albumTracks.value = {...albumTracks.value, [albumId]: response.data.tracks || []}
      } catch (error) {
        console.error(error)
        showErrorToast('Error loading album tracks')
      } finally {
        albumTracksLoading.value = {...albumTracksLoading.value, [albumId]: false}
      }
    }

    const toggleAlbumTracks = async data => {
      const albumId = data.item.yandex_id

      if (!data.detailsShowing && !albumTracks.value[albumId]) {
        await loadAlbumTracks(albumId)
      }
      data.toggleDetails()
    }

    const addAlbumTrack = async (albumId, track) => {
      albumTracksLoading.value = {...albumTracksLoading.value, [albumId]: true}
      try {
        await createTrackInDb(track)
      } catch (error) {
        console.error(error)
        showErrorToast('Error adding track')
        albumTracksLoading.value = {...albumTracksLoading.value, [albumId]: false}
        return
      }
      // Перезагрузка списка альбома подтянет свежие ссылки на БД.
      await loadAlbumTracks(albumId)
    }

    fetchFeedData()

    return {
      isLoading,
      loadError,
      isEmpty,
      playlists,
      days,
      playlistColumns,
      albumColumns,
      albumTracks,
      albumTracksLoading,
      addTrack,
      toggleAlbumTracks,
      addAlbumTrack,
      avatarText,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
